import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpApiAdminService } from './http-api.service';

/** Respuesta del backend al pedir una URL firmada (`/clientes/*-firmada`). */
interface ISignedUpload {
  uploadUrl: string;
  publicUrl: string;
  objectName: string;
  headers: Record<string, string>;
  expiresAt: string;
}

/** Mime por extensión: fallback cuando `file.type` viene vacío (típico en fuentes). */
const MIME_POR_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  svg: 'image/svg+xml',
  otf: 'font/otf',
  ttf: 'font/ttf',
};

function contentTypeDe(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  // La extensión MANDA para los tipos que aceptamos: el browser a veces reporta mal el
  // `file.type` (ej. .otf → 'application/vnd.oasis.opendocument.formula-template', que el
  // backend rechaza). Para extensiones desconocidas, caemos a `file.type`.
  if (MIME_POR_EXT[ext]) {
    return MIME_POR_EXT[ext];
  }
  return file.type || 'application/octet-stream';
}

/**
 * Servicio ÚNICO de subida de imágenes. Centraliza lo que antes estaba duplicado en
 * clientes/botones/porticos/lectura-portico.
 *
 * Flujo (signed-URL): pide una URL firmada al backend (rápido, sin bytes) y sube el
 * archivo DIRECTO al bucket GCS con un PUT. No proxea los bytes por el backend Nest.
 *
 * El PUT usa `HttpClient` crudo (NO `HttpService`): la URL firmada no debe llevar
 * `Authorization` o GCS rechaza la firma. Mismo patrón que `OsmService`.
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private api = inject(HttpApiAdminService);
  private http = inject(HttpClient);

  /**
   * Sube una imagen de cliente al path determinístico
   * `img/<ENV>/<nombreCliente>/<nombreCategoria>/<nombreImagen>` (reemplaza el viejo
   * `POST /clientes/cargarimagen` multipart). Devuelve el `publicUrl`.
   */
  public async subirImagenCliente(
    file: File,
    nombreCliente: string,
    nombreImagen: string,
    nombreCategoria?: string,
  ): Promise<{ url: string }> {
    const firma: ISignedUpload = await firstValueFrom(
      this.api.post(`/clientes/cargarimagen-firmada`, {
        nombreCliente,
        nombreCategoria,
        nombreImagen,
        contentType: contentTypeDe(file),
      }),
    );
    return this.subirAlBucket(firma, file);
  }

  /**
   * Sube una imagen pública con path libre `img/<ENV>/<fileName>` (reemplaza el viejo
   * `POST /clientes/subirImagen` multipart). Devuelve el `publicUrl`.
   */
  public async subirImagenPublica(
    file: File,
    fileName: string,
  ): Promise<{ url: string }> {
    const firma: ISignedUpload = await firstValueFrom(
      this.api.post(`/clientes/subirimagen-firmada`, {
        fileName,
        contentType: contentTypeDe(file),
      }),
    );
    return this.subirAlBucket(firma, file);
  }

  /** PUT directo a GCS con los headers firmados (sin auth). Devuelve el publicUrl. */
  private async subirAlBucket(
    firma: ISignedUpload,
    file: File,
  ): Promise<{ url: string }> {
    await firstValueFrom(
      this.http.put(firma.uploadUrl, file, { headers: firma.headers }),
    );
    return { url: firma.publicUrl };
  }
}
